import { action }          from 'mobx';
import { computed }        from 'mobx';
import { observable }      from 'mobx';
import { makeObservable }  from 'mobx';
import { MessageTemplate } from '@/services/MercureListener';
import userMercureListener from '@/services/userMercureListener';

export type MercureEventStatus = 'progress' | 'done' | 'error' | 'info';

/**
 * Regroupement par préfixe de kind. Peu stable : à terme il vaudrait mieux que le back envoie
 * deux champs distincts (type de traitement + action) plutôt qu'un kind suffixé.
 */
export const getKindFamily = (kind: string): string => kind.replace(/[-_](progress|failed|completed)$/, '');

export type MercureEvent = {
	/** Clé unique = objectUrn si présent, sinon kind : permet de mettre à jour un événement en place. */
	id: string;
	kind: string;
	objectUrn: Urn | null;
	status: MercureEventStatus;
	current?: number;
	total?: number;
	percent?: number;
	data: any;
	createdAt: number;
	updatedAt: number;
};

export type MercureEventGroup = {
	kind: string;
	events: MercureEvent[];
};

const interpret = (message: MessageTemplate): Pick<MercureEvent, 'status' | 'current' | 'total' | 'percent'> => {
	const data = message.data ?? {};

	switch (message.kind) {
		// Import des places : progression avec current / total.
		case 'import_places_progress':
		case 'validate_import_places_progress': {
			const total = typeof data.total === 'number' ? data.total : 0;
			const current = typeof data.current === 'number' ? data.current : 0;
			const percent = total ? Math.floor((current / total) * 100) : 0;

			return {
				status: total > 0 && current >= total ? 'done' : 'progress',
				current,
				total,
				percent,
			};
		}

		// Fin d'un traitement : bascule le bloc en terminé.
		case 'import_places_completed':
		case 'validate_import_places_completed':
			return { status: 'done', percent: 100 };

		// Échec d'un traitement : bascule le bloc en erreur.
		case 'import_places_failed':
		case 'validate_import_places_failed':
			return { status: 'error' };

		default:
			return { status: 'info' };
	}
};

class MercureEventsStore {
	private _events = observable.map<string, MercureEvent>();

	constructor() {
		makeObservable(this);

		userMercureListener.onAny(message => {
			this.handle(message);
		});
	}

	@action handle(message: MessageTemplate): void {
		if (!message?.kind) {
			return;
		}

		// Regroupement par objectUrn : tous les messages portant sur un même objet mettent à jour le même bloc.
		const id = message.objectUrn ? `${message.objectUrn}` : message.kind;
		const now = Date.now();
		const existing = this._events.get(id);
		const interpreted = interpret(message);

		this._events.set(id, {
			id,
			kind: message.kind,
			objectUrn: message.objectUrn ?? null,
			data: message.data,
			createdAt: existing?.createdAt ?? now,
			updatedAt: now,
			status: interpreted.status,
			// On conserve la progression précédente si le nouveau message ne la fournit pas (ex : *_failed).
			current: interpreted.current ?? existing?.current,
			total: interpreted.total ?? existing?.total,
			percent: interpreted.percent ?? existing?.percent,
		});
	}

	@computed get events(): MercureEvent[] {
		// Tri par date d'ajout : trier par date de màj ferait sauter les blocs de place quand plusieurs tournent en parallèle.
		return Array.from(this._events.values())
			.sort((a, b) => b.createdAt - a.createdAt);
	}

	/** Groupes d'événements par famille de kind. */
	@computed get groups(): MercureEventGroup[] {
		const map = new Map<string, MercureEvent[]>();

		for (const event of this.events) {
			const family = getKindFamily(event.kind);
			const events = map.get(family) ?? [];
			events.push(event);
			map.set(family, events);
		}

		return Array.from(map.entries())
			.map(([kind, events]) => ({ kind, events }))
			.sort((a, b) => (b.events[0]?.createdAt ?? 0) - (a.events[0]?.createdAt ?? 0));
	}

	@computed get hasEvents(): boolean {
		return this._events.size > 0;
	}

	/** Nombre d'événements en cours, utilisé pour le badge. */
	@computed get activeCount(): number {
		return this.events.filter(event => event.status === 'progress').length;
	}

	@action remove(id: string): void {
		this._events.delete(id);
	}

	@action clear(): void {
		this._events.clear();
	}
}

export default new MercureEventsStore();
